import React, { useState } from 'react';
import { Calendar, Clock, AlertTriangle, Power } from 'lucide-react';

export const Maintenance: React.FC = () => {
  const [maintenanceType, setMaintenanceType] = useState('scheduled');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState('');
  const [message, setMessage] = useState('');

  const currentMaintenance = {
    active: true,
    startTime: '2024-01-20 02:00 AM UTC',
    endTime: '2024-01-20 04:00 AM UTC',
    message: 'Scheduled database maintenance and updates',
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard Maintenance</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Schedule or unschedule maintenance windows for the dashboard
        </p>
      </div>

      {currentMaintenance.active && (
        <div className="card-modern p-4 bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-600 dark:text-amber-400">Active Maintenance</h3>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                {currentMaintenance.message}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                {currentMaintenance.startTime} - {currentMaintenance.endTime}
              </p>
              <button className="mt-3 text-sm font-medium text-amber-600 dark:text-amber-400 hover:underline">
                Cancel Maintenance
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card-modern p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Maintenance Configuration</h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Maintenance Type
              </label>
              <div className="grid gap-3 md:grid-cols-2">
                <label className={`card-modern p-4 cursor-pointer transition-all ${
                  maintenanceType === 'scheduled' ? 'ring-2 ring-primary' : ''
                }`}>
                  <input
                    type="radio"
                    name="type"
                    value="scheduled"
                    checked={maintenanceType === 'scheduled'}
                    onChange={(e) => setMaintenanceType(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Scheduled Maintenance</p>
                      <p className="text-xs text-muted-foreground">Plan maintenance in advance</p>
                    </div>
                  </div>
                </label>

                <label className={`card-modern p-4 cursor-pointer transition-all ${
                  maintenanceType === 'immediate' ? 'ring-2 ring-primary' : ''
                }`}>
                  <input
                    type="radio"
                    name="type"
                    value="immediate"
                    checked={maintenanceType === 'immediate'}
                    onChange={(e) => setMaintenanceType(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center space-x-3">
                    <Power className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Immediate Maintenance</p>
                      <p className="text-xs text-muted-foreground">Start maintenance now</p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {maintenanceType === 'scheduled' && (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="input-modern h-10 w-full px-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Start Time (UTC)
                    </label>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="input-modern h-10 w-full px-3 text-sm"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">
                Duration
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="input-modern h-10 w-full px-3 text-sm"
              >
                <option value="">Select duration...</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
                <option value="180">3 hours</option>
                <option value="240">4 hours</option>
                <option value="custom">Custom duration</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Maintenance Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="input-modern w-full px-3 py-2 text-sm resize-none"
                placeholder="Describe the maintenance activities..."
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h2 className="text-lg font-semibold mb-4">Notification Settings</h2>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input type="checkbox" className="rounded border-border" defaultChecked />
              <span className="text-sm">Send email notifications to all users</span>
            </label>
            <label className="flex items-center space-x-3">
              <input type="checkbox" className="rounded border-border" defaultChecked />
              <span className="text-sm">Display banner on dashboard</span>
            </label>
            <label className="flex items-center space-x-3">
              <input type="checkbox" className="rounded border-border" />
              <span className="text-sm">Restrict dashboard access during maintenance</span>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end pt-4">
          <button className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <span>{maintenanceType === 'immediate' ? 'Start Maintenance' : 'Schedule Maintenance'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}; 